import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { WithTranslation, withTranslation } from 'react-i18next';

import ContextualHelpModal from '../ContextualHelpModal';
import EmploymentInput from './EmploymentInput';
import InfoLabel from '../ContextualHelpModal/InfoLabel';
import LabeledInput from '../LabeledInput';
import MailingAddressInput from './MailingAddressInput';
import PhoneInput from '../PhoneInput';
import RelationshipPicker from './RelationshipPicker';
import { DatePicker } from '../DatePicker';
import { Email } from '../Question/Email';
import Enum from '../Question/Enum';
import SameAsPatientOrCustom from '../SameAsPatientOrCustom';
import { SelectInput } from '../SelectInput';
import { TextInput } from '../TextInput';

import * as api from '../../api';
import { Forms, FormSetSelected } from '../../reducers/store';
import { isEnumMember } from 'common/dist/utils/types';
import {
  getEnumOptions,
  getGuarantorsFromInsurancePrimitive,
  getPatientDetails,
  mergeValues,
} from './utils';
import {
  getSelectedForms,
  getSelectedPracticePreference,
} from '../../utils/formSet';
import * as logger from '../../logger';

import styles from './index.module.css';
import defaultStyles from '../../styles/formSetStyles.module.css';

interface StateProps {
  employmentStatusOptions: api.PracticePreference.EmploymentStatus[];
  patientRelationshipsToOptions: api.PracticePreference.RelationshipPreference;
  patientGuarantorDetailsEnabled: boolean;
  previsitMergeSubscriberGuarantorsEnabled: boolean;
  patient: api.Patient.Patient;
  patientAge?: number;
  forms: Forms;
}

function mapStateToProps(store: FormSetSelected): StateProps {
  const selectedPracticePreference = getSelectedPracticePreference(store);
  return {
    patientGuarantorDetailsEnabled:
      selectedPracticePreference.patientGuarantorDetailsEnabled,
    patientRelationshipsToOptions:
      selectedPracticePreference.patientRelationshipsToGuarantor,
    employmentStatusOptions: selectedPracticePreference.employmentStatuses,
    previsitMergeSubscriberGuarantorsEnabled:
      selectedPracticePreference.previsitMergeSubscriberGuarantorsEnabled,
    forms: getSelectedForms(store.formSets, store.selectedFormSetId),
    patient: store.loggedInPatient,
    patientAge: store.loggedInPatient?.dateOfBirth
      ? moment().diff(store.loggedInPatient.dateOfBirth, 'years')
      : undefined,
  };
}

interface Props extends StateProps, WithTranslation {
  value: api.Guarantor.PrevisitGuarantorFormFieldValue;
  updateFieldValue: (value: string) => void;
  saveFieldValue: (value: string) => void;
  employmentStatusOptions: api.PracticePreference.EmploymentStatus[];
  helpDialog: string | null;
}

interface State {
  guarantorsById: Record<string, api.Guarantor.PrevisitGuarantor>;
  guarantorOrdering: string[];
  selectedGuarantorId: string | null;
  showHelp: boolean;
}

export function getBlankGuarantor(): Omit<
  api.Guarantor.PrevisitGuarantor,
  'id' | 'newlyAdded'
> {
  return {
    deletedTimestamp: null,
    ehrId: null,
    ehrInformationId: null,
    firstName: null,
    lastName: null,
    sex: null,
    dateOfBirth: null,
    race: null,
    raceRaw: null,
    ssn4: null,
    kind: api.Guarantor.Kind.PersonalFamily,
    relationshipToPatient: null,
    relationshipToPatientRaw: null,
    phoneNumberHome: null,
    phoneNumberMobile: null,
    phoneNumberWork: null,
    emailAddress: null,
    employer: null,
    employmentStatus: null,
    employmentStatusRaw: null,
    addressId: null,
    address: null,
    retirementDate: null,
    created: new Date().toISOString(),
    updated: new Date().toISOString(),
  };
}

class Guarantor extends React.Component<Props, State> {
  state: State = {
    guarantorOrdering: [],
    guarantorsById: {},
    selectedGuarantorId: null,
    showHelp: false,
  };

  componentDidMount() {
    const {
      forms,
      value,
      patientAge,
      previsitMergeSubscriberGuarantorsEnabled,
    } = this.props;
    // retrieve potential guarantors from subscriber answers
    let subscriberGuarantors: api.Guarantor.PrevisitGuarantor[] = [];
    if (previsitMergeSubscriberGuarantorsEnabled) {
      subscriberGuarantors = getGuarantorsFromInsurancePrimitive(forms);
    }
    const maybeMergedGuarantors = mergeValues(
      value.guarantors,
      subscriberGuarantors,
    );

    const guarantorsById = _.mapKeys(maybeMergedGuarantors, 'id');
    const guarantorOrdering = maybeMergedGuarantors.map((g) => g.id);
    let selectedGuarantorId = value.selectedGuarantorId ?? null;

    const { options, someoneElseId } = this.memoizedEnumOptions(
      guarantorOrdering,
      guarantorsById,
      patientAge,
    );

    this.setState(
      {
        guarantorsById,
        guarantorOrdering,
        selectedGuarantorId,
      },
      () => {
        if (
          options.length === 1 &&
          options[0].value === someoneElseId &&
          selectedGuarantorId !== someoneElseId
        ) {
          this.handleSelectedChange(someoneElseId, true);
        }
      },
    );
  }

  saveField = async (args: { isSomeoneElse?: boolean } = {}) => {
    const { isSomeoneElse } = args;
    const { value } = this.props;
    const { guarantorsById, guarantorOrdering, selectedGuarantorId } =
      this.state;
    const newValue: api.Guarantor.PrevisitGuarantorFormFieldValue = {
      ...value,
      ...(isSomeoneElse !== undefined && { isSomeoneElse }),
      guarantors: guarantorOrdering
        .map((id) => guarantorsById[id])
        .filter((item) => !!item),
      selectedGuarantorId,
    };

    if (_.isEqual(value, newValue)) return;

    try {
      await this.props.updateFieldValue(JSON.stringify(newValue));
      await this.props.saveFieldValue(JSON.stringify(newValue));
    } catch (err) {
      logger.error(err, 'failed to update values');
    }
  };

  handleSelectedChange = (selectedId: string, isSomeoneElse: boolean) => {
    const { guarantorsById, guarantorOrdering } = this.state;

    if (!selectedId) return this.setState({ selectedGuarantorId: null });

    const exists = selectedId in guarantorsById;

    if (exists) {
      return this.setState({ selectedGuarantorId: selectedId }, () => {
        this.saveField({ isSomeoneElse });
      });
    }

    this.setState(
      {
        selectedGuarantorId: selectedId,
        guarantorsById: {
          ...guarantorsById,
          [selectedId]: {
            ...getBlankGuarantor(),
            id: selectedId,
            newlyAdded: true,
          },
        },
        guarantorOrdering: [...guarantorOrdering, selectedId],
      },
      () => {
        this.saveField({ isSomeoneElse });
      },
    );
  };

  handleGuarantorUpdate = (
    id: string,
    values: Partial<api.Guarantor.PrevisitGuarantor>,
    withSave?: boolean,
  ) => {
    const { guarantorsById } = this.state;

    this.setState(
      {
        guarantorsById: {
          ...guarantorsById,
          [id]: {
            ...guarantorsById[id],
            ...values,
          },
        },
      },
      () => {
        if (!withSave) return;
        this.saveField();
      },
    );
  };

  memoizedPatientDetails = _.memoize(getPatientDetails);

  memoizedEnumOptions = _.memoize(
    (
      guarantorOrdering: string[],
      guarantorsById: Record<string, api.Guarantor.PrevisitGuarantor>,
      patientAge?: number,
    ) => {
      const selfContact = _.find(
        guarantorsById,
        (g) => g.relationshipToPatient === api.Common.Relationship.Self,
      );
      return getEnumOptions({
        patientAge,
        contacts: guarantorOrdering
          .map((id) => guarantorsById[id])
          .filter((item) => !!item),
        selfContact,
      });
    },
  );

  setShowHelp = (value: boolean) => this.setState({ showHelp: value });

  renderGuarantorSelect() {
    const { patientAge, t } = this.props;
    const { guarantorOrdering, guarantorsById, selectedGuarantorId } =
      this.state;

    const selfContact = _.find(
      guarantorsById,
      (g) => g.relationshipToPatient === api.Common.Relationship.Self,
    );

    const { options, someoneElseId } = this.memoizedEnumOptions(
      guarantorOrdering,
      guarantorsById,
      patientAge,
    );

    // hide the enum selection altogether if "someone else" is the only answer
    const hasSomeoneElseOnly =
      options.length === 1 && options[0].value === someoneElseId;
    const hasSelfOption = options.find((o) => o.value === selfContact?.id);

    return (
      <>
        <label className={defaultStyles.Label}>
          {t('whoIsResponsibleForPayingThisVisit')}
        </label>
        {!hasSomeoneElseOnly && (
          <Enum
            listClassName={classNames(styles.Enum, {
              [styles.FirstOptionPatient]: hasSelfOption,
            })}
            options={options}
            value={selectedGuarantorId || ''}
            onChange={(value) => {
              const isSomeoneElse = value === someoneElseId;
              this.handleSelectedChange(value, isSomeoneElse);
            }}
          />
        )}
      </>
    );
  }

  renderForm() {
    const {
      employmentStatusOptions,
      patientGuarantorDetailsEnabled,
      patientRelationshipsToOptions,
      t,
    } = this.props;

    const { selectedGuarantorId, guarantorsById } = this.state;

    const selectedGuarantor = selectedGuarantorId
      ? guarantorsById[selectedGuarantorId]
      : null;

    if (
      !patientGuarantorDetailsEnabled ||
      !selectedGuarantorId ||
      !selectedGuarantor ||
      selectedGuarantor.relationshipToPatient === api.Common.Relationship.Self
    ) {
      return null;
    }
    const { patientAddressDetails, patientCellPhoneNumber } =
      this.memoizedPatientDetails(this.props.forms, this.props.patient);

    if (!selectedGuarantor.newlyAdded) {
      const contactName = [
        selectedGuarantor.firstName,
        selectedGuarantor.lastName,
      ].join(' ');
      return (
        <>
          <div>
            <label className={defaultStyles.Label}>
              {t('reviewContactName', { contactName })}
            </label>

            <MailingAddressInput
              onChange={(address) =>
                this.handleGuarantorUpdate(selectedGuarantorId, { address })
              }
              value={selectedGuarantor.address ?? null}
              patientAddress={patientAddressDetails ?? null}
            />
          </div>
          <div>
            <RelationshipPicker
              onChange={(relationshipToPatient) => {
                this.handleGuarantorUpdate(selectedGuarantorId, {
                  relationshipToPatient,
                });
              }}
              relationshipOptions={patientRelationshipsToOptions}
              value={selectedGuarantor.relationshipToPatient}
              associatedEntity={contactName ?? t('thePersonPayingForThisVisit')}
            />
          </div>
        </>
      );
    }

    return (
      <div className={styles.ResponsibleContactInfo}>
        <LabeledInput label={t('firstName')} htmlFor="guarantor-first-name">
          <TextInput
            id="guarantor-first-name"
            placeholder={t('firstName')}
            value={selectedGuarantor.firstName || ''}
            onChange={(e) =>
              this.handleGuarantorUpdate(selectedGuarantorId, {
                firstName: e.target.value,
              })
            }
            onBlur={(e) =>
              this.handleGuarantorUpdate(
                selectedGuarantorId,
                {
                  firstName: e.target.value,
                },
                true,
              )
            }
          />
        </LabeledInput>

        <LabeledInput label={t('lastName')} htmlFor="guarantor-last-name">
          <TextInput
            id="guarantor-last-name"
            placeholder={t('lastName')}
            value={selectedGuarantor.lastName || ''}
            onChange={(e) =>
              this.handleGuarantorUpdate(selectedGuarantorId, {
                lastName: e.target.value,
              })
            }
            onBlur={(e) => {
              this.handleGuarantorUpdate(
                selectedGuarantorId,
                {
                  lastName: e.target.value,
                },
                true,
              );
            }}
          />
        </LabeledInput>

        <LabeledInput label={t('sex')} htmlFor="guarantor-sex">
          <SelectInput
            id="guarantor-sex"
            onChange={(sex: string) => {
              if (!isEnumMember(api.Previsit.Sex, sex)) {
                return;
              }
              this.handleGuarantorUpdate(selectedGuarantorId, { sex });
            }}
            options={api.Previsit.DefaultSexes.map((sex) => ({
              display: api.Previsit.sexOptionsMap[sex],
              key: sex,
              value: sex,
            }))}
            value={selectedGuarantor.sex?.toString() || ''}
          />
        </LabeledInput>

        <LabeledInput label={t('dateOfBirth')} htmlFor="guarantor-dob">
          <DatePicker
            curvedInput={5}
            date={
              selectedGuarantor.dateOfBirth &&
              !isNaN(new Date(selectedGuarantor.dateOfBirth).valueOf())
                ? new Date(selectedGuarantor.dateOfBirth).toISOString()
                : ''
            }
            onChange={(dateOfBirth) =>
              this.handleGuarantorUpdate(selectedGuarantorId, { dateOfBirth })
            }
          />
        </LabeledInput>

        <MailingAddressInput
          onChange={(address) =>
            this.handleGuarantorUpdate(selectedGuarantorId, { address })
          }
          value={selectedGuarantor.address ?? null}
          patientAddress={patientAddressDetails ?? null}
        />

        <LabeledInput label={t('phoneNumber')} htmlFor="guarantor-phone-number">
          <SameAsPatientOrCustom
            customInputRender={(isDisabled) => (
              <PhoneInput
                label={t('phoneNumber')}
                id="guarantor-phone-number"
                value={selectedGuarantor.phoneNumberHome || ''}
                updateFieldValue={(phoneNumberHome) =>
                  this.handleGuarantorUpdate(selectedGuarantorId, {
                    phoneNumberHome,
                  })
                }
                saveFieldValue={(phoneNumberHome) => {
                  this.handleGuarantorUpdate(
                    selectedGuarantorId,
                    {
                      phoneNumberHome,
                    },
                    true,
                  );
                }}
                disabled={isDisabled}
              />
            )}
            customElementName="phone number"
            value={selectedGuarantor.phoneNumberHome}
            patientValue={patientCellPhoneNumber || ''}
            onSameAsPatientClicked={() => {
              this.handleGuarantorUpdate(selectedGuarantorId, {
                phoneNumberHome: patientCellPhoneNumber,
              });
            }}
          />
        </LabeledInput>

        <LabeledInput
          label={t('emailAddress')}
          htmlFor="guarantor-email-address"
        >
          <Email
            label={t('emailAddress')}
            id="guarantor-email-address"
            value={selectedGuarantor.emailAddress || ''}
            saveFieldValue={(emailAddress) => {
              this.handleGuarantorUpdate(
                selectedGuarantorId,
                {
                  emailAddress,
                },
                true,
              );
            }}
            updateFieldValue={(emailAddress) =>
              this.handleGuarantorUpdate(selectedGuarantorId, { emailAddress })
            }
            placeholder={t('placeholderEmail')}
          />
        </LabeledInput>

        <EmploymentInput
          retirementDate={selectedGuarantor.retirementDate}
          employmentStatus={selectedGuarantor.employmentStatus}
          employer={selectedGuarantor.employer ?? null}
          employmentStatusOptions={employmentStatusOptions}
          onChange={(values) =>
            this.handleGuarantorUpdate(selectedGuarantorId, values)
          }
          onSave={(values) =>
            this.handleGuarantorUpdate(selectedGuarantorId, values, true)
          }
        />

        <RelationshipPicker
          onChange={(relationshipToPatient) => {
            const updateValues = {
              relationshipToPatient,
              relationshipToPatientRaw: relationshipToPatient,
            };
            this.handleGuarantorUpdate(selectedGuarantorId, updateValues, true);
          }}
          relationshipOptions={patientRelationshipsToOptions}
          value={selectedGuarantor.relationshipToPatient}
          associatedEntity={t('thePersonPayingForThisVisit')}
        />
      </div>
    );
  }

  render() {
    const { helpDialog, t } = this.props;
    const { showHelp } = this.state;

    return (
      <div>
        {helpDialog && (
          <ContextualHelpModal
            headerText={t('moreInformation')}
            helpText={helpDialog}
            isOpen={showHelp}
            onRequestClose={() => this.setShowHelp(false)}
            onHandleHelpful={async () => this.setShowHelp(false)}
          />
        )}
        <div>
          {this.renderGuarantorSelect()}
          {helpDialog && (
            <InfoLabel
              className={styles.InfoLabel}
              onClick={() => this.setShowHelp(true)}
              label="Not sure?"
            />
          )}
          {this.renderForm()}
        </div>
      </div>
    );
  }
}

export default connect(mapStateToProps)(withTranslation()(Guarantor));
